import { useState } from "react";
import type { ChangeEvent } from "react";
import { Button, Form, Input, Popconfirm, Tag, Tooltip } from "antd";
import { ThunderboltOutlined } from "@ant-design/icons";
import { ActivityType } from "../../../../types";

const { TextArea } = Input;

export type PlanningSummaryPanelVariant = "summary" | "superSummary";

const PANEL_COPY: Record<
  PlanningSummaryPanelVariant,
  {
    testIdPrefix: string;
    label: string;
    placeholder: string;
    confirmTitle: string;
  }
> = {
  summary: {
    testIdPrefix: "planning-summary",
    label: "Resumen para vista de planeación",
    placeholder: "Resumen para vista de planeación...",
    confirmTitle:
      "¿Deseas reemplazar el resumen actual? Esta acción no se puede deshacer.",
  },
  superSummary: {
    testIdPrefix: "planning-super-summary",
    label: "Resumen súper resumido (planeación transversal)",
    placeholder: "Resumen súper resumido (planeación transversal)...",
    confirmTitle:
      "¿Deseas reemplazar el resumen súper resumido actual? Esta acción no se puede deshacer.",
  },
};

interface PlanningSummaryPanelProps {
  variant?: PlanningSummaryPanelVariant;
  activityType: ActivityType;
  text: string;
  /** Only the super summary can go stale; it is the one that passes this. */
  stale?: boolean;
  loading: boolean;
  disabled: boolean;
  onChangeText: (text: string) => void;
  onGenerate: () => void;
}

// Popconfirm is fully controlled here: replacing non-empty text needs
// confirmation, but generating over an empty section must fire immediately
// with no dialog at all — an uncontrolled Popconfirm would open on every click.
const PlanningSummaryPanel: React.FC<PlanningSummaryPanelProps> = ({
  variant = "summary",
  activityType,
  text,
  stale = false,
  loading,
  disabled,
  onChangeText,
  onGenerate,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const copy = PANEL_COPY[variant];

  const handleGenerateClick = () => {
    if (text) {
      setConfirmOpen(true);
      return;
    }
    onGenerate();
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    onGenerate();
  };

  // A plain string label keeps antd's native `title` attribute on the label.
  const label = stale ? (
    <>
      {copy.label}
      <Tooltip title="El contenido de esta sección cambió después de generar este resumen. Puedes regenerarlo.">
        <Tag color="warning" style={{ marginLeft: "8px" }}>
          Desactualizado
        </Tag>
      </Tooltip>
    </>
  ) : (
    copy.label
  );

  return (
    <div
      className="planning-summary-panel"
      data-testid={`${copy.testIdPrefix}-${activityType}`}
    >
      <Form.Item label={label} className="planning-summary">
        <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
          <TextArea
            placeholder={copy.placeholder}
            value={text}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
              onChangeText(e.target.value)
            }
            rows={3}
            style={{ flex: 1 }}
          />
          <Popconfirm
            title={copy.confirmTitle}
            open={confirmOpen}
            onConfirm={handleConfirm}
            onCancel={() => setConfirmOpen(false)}
            okText="Reemplazar"
            cancelText="Cancelar"
          >
            <Button
              type="primary"
              icon={<ThunderboltOutlined />}
              loading={loading}
              disabled={disabled}
              onClick={handleGenerateClick}
            >
              Generar con IA
            </Button>
          </Popconfirm>
        </div>
      </Form.Item>
    </div>
  );
};

export default PlanningSummaryPanel;
